/**
 * Coordinate maths for showing a suggested value where it sits in the document.
 *
 * Extend returns each citation as a polygon in the coordinate space of the page
 * it was read from, sized in points. A rendered page is some other size, so every
 * number has to be scaled by the ratio between them before it means anything on
 * screen.
 *
 * All of it lives here for one reason: the polygon's vertical origin is the one
 * thing the vendor does not document. The side we render onto is settled -- the
 * viewer places a page top-down, so y grows the way CSS does -- and the other side has
 * only Extend's own `extend_edit:bbox` coordinates to go on, which the USCIS form
 * overlay in `ForeignNationalUscisFormsPanel` treats as measured down from the
 * top-left against a 612x792 page, and which ships working. `scalePoint` assumes
 * the citation polygon matches. If a highlight ever lands mirrored vertically,
 * that one function is the whole fix.
 */

/** Floor for a highlight's size, so a degenerate polygon is still visible. */
const MIN_HIGHLIGHT_SIZE = 6;

export type CitationPoint = { x: number; y: number };

/** A box in rendered-page pixels, ready to hand to CSS. */
export type CitationRect = {
  left: number;
  top: number;
  width: number;
  height: number;
};

export type CitationGeometry = {
  polygon?: CitationPoint[] | null;
  pageWidth?: number | null;
  pageHeight?: number | null;
};

type RenderedPageSize = {
  renderedPageWidth: number;
  renderedPageHeight: number;
};

/**
 * The polygon as a box on the rendered page, or `null` when it cannot be placed.
 *
 * Returning `null` rather than a guess is deliberate: a highlight in the wrong
 * place is worse than no highlight, because it tells the client the value came
 * from somewhere it did not. Callers fall back to quoting the line.
 */
export function citationHighlightRect(
  geometry: CitationGeometry,
  { renderedPageWidth, renderedPageHeight }: RenderedPageSize
): CitationRect | null {
  const points = usablePoints(geometry.polygon);
  const pageWidth = positiveNumber(geometry.pageWidth);
  const pageHeight = positiveNumber(geometry.pageHeight);

  // The page size is not optional context: without it the polygon has no scale.
  if (points.length === 0 || pageWidth === null || pageHeight === null)
    return null;
  if (!(renderedPageWidth > 0) || !(renderedPageHeight > 0)) return null;

  const scaled = points.map((point) =>
    scalePoint(point, {
      scaleX: renderedPageWidth / pageWidth,
      scaleY: renderedPageHeight / pageHeight,
      renderedPageHeight
    })
  );
  const xs = scaled.map((point) => point.x);
  const ys = scaled.map((point) => point.y);

  // The bounding box of every point, so a multi-point or slightly rotated
  // polygon still yields one rectangle covering the whole cited region.
  const left = clamp(Math.min(...xs), 0, renderedPageWidth);
  const right = clamp(Math.max(...xs), 0, renderedPageWidth);
  const top = clamp(Math.min(...ys), 0, renderedPageHeight);
  const bottom = clamp(Math.max(...ys), 0, renderedPageHeight);

  return {
    left,
    top,
    // A degenerate polygon (one point, or a zero-height line) would otherwise be
    // invisible, which reads as "no citation" rather than "here".
    width: Math.max(right - left, MIN_HIGHLIGHT_SIZE),
    height: Math.max(bottom - top, MIN_HIGHLIGHT_SIZE)
  };
}

/**
 * How far to shift a rendered page inside a crop box to centre the highlight.
 *
 * The inline snippet is a small window onto a full page: the page is rendered at
 * full size and moved behind a clipping box. The shift is clamped to the page's
 * own edges so the crop never shows empty space beside the document, which is why
 * a highlight near a margin sits off-centre rather than floating.
 */
export function citationSnippetOffset({
  rect,
  viewportWidth,
  viewportHeight,
  renderedPageWidth,
  renderedPageHeight
}: {
  rect: CitationRect;
  viewportWidth: number;
  viewportHeight: number;
} & RenderedPageSize): { offsetX: number; offsetY: number } {
  return {
    offsetX: negated(
      shift(rect.left + rect.width / 2, viewportWidth, renderedPageWidth)
    ),
    offsetY: negated(
      shift(rect.top + rect.height / 2, viewportHeight, renderedPageHeight)
    )
  };
}

/**
 * How tall the cited page renders at a given width, or `null` if unknown.
 *
 * Computed from the page's own proportions rather than read off whatever the
 * viewer reports, because the page is sized by its width and the height follows
 * the document. A viewer that assumes US Letter would misplace the highlight on
 * a passport scan, which is rarely that shape.
 *
 * Assumes the page is not rotated: a renderer swaps a page's dimensions for a
 * quarter-turn `/Rotate`, and these dimensions come from the extractor, which
 * reports the page as it read it.
 */
export function renderedPageHeightFor(
  geometry: CitationGeometry,
  renderedPageWidth: number
): number | null {
  const pageWidth = positiveNumber(geometry.pageWidth);
  const pageHeight = positiveNumber(geometry.pageHeight);
  if (pageWidth === null || pageHeight === null) return null;
  if (!(renderedPageWidth > 0)) return null;

  return renderedPageWidth * (pageHeight / pageWidth);
}

/** Where on its page the citation sits, as a 0-1 ratio, for scrolling to it. */
export function citationPageOffsetRatio(
  geometry: CitationGeometry
): number | null {
  const points = usablePoints(geometry.polygon);
  const pageHeight = positiveNumber(geometry.pageHeight);
  if (points.length === 0 || pageHeight === null) return null;

  const top = Math.min(...points.map((point) => point.y));
  return clamp(top / pageHeight, 0, 1);
}

/**
 * One polygon point in rendered-page pixels.
 *
 * The single place the vertical origin is decided. Extend's y grows downwards
 * from the top of the page, as the viewer and CSS both do, so this is a plain scale.
 * Were it ever to grow upwards from the bottom, the fix is here and nowhere else:
 * `y: renderedPageHeight - point.y * scaleY`.
 */
function scalePoint(
  point: CitationPoint,
  {
    scaleX,
    scaleY
  }: { scaleX: number; scaleY: number; renderedPageHeight: number }
): CitationPoint {
  return { x: point.x * scaleX, y: point.y * scaleY };
}

function usablePoints(
  polygon: CitationPoint[] | null | undefined
): CitationPoint[] {
  if (!Array.isArray(polygon)) return [];
  return polygon.filter(
    (point) =>
      point != null &&
      Number.isFinite(Number(point.x)) &&
      Number.isFinite(Number(point.y))
  );
}

function positiveNumber(value: number | null | undefined): number | null {
  const parsed = Number(value);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
}

function clamp(value: number, min: number, max: number): number {
  return Math.min(Math.max(value, min), max);
}

/** Centre `center` in a `viewport`-sized window, without leaving `total`. */
function shift(center: number, viewport: number, total: number): number {
  if (!(viewport > 0)) return 0;
  return clamp(center - viewport / 2, 0, Math.max(0, total - viewport));
}

/** Negation that keeps zero positive, so callers can compare against `0`. */
function negated(value: number): number {
  return value === 0 ? 0 : -value;
}
